import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Typography, Space, Tag, Spin } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { CloudOutlined, ThunderboltOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const { Title, Paragraph } = Typography;
const { Option } = Select;

// 模拟气象数据
const generateWeatherData = (source: string) => {
    const baseData = [];
    const now = new Date();

    for (let i = 0; i < 24; i++) {
        const time = new Date(now.getTime() + i * 60 * 60 * 1000);
        const hour = time.getHours();

        // 根据数据源生成不同的数据特征
        let tempVariation = 0;
        let windVariation = 0;
        let humidityVariation = 0;

        switch (source) {
            case 'guangxi':
                tempVariation = Math.sin(hour * Math.PI / 12) * 5 + Math.random() * 2;
                windVariation = Math.sin((hour + 6) * Math.PI / 12) * 3 + Math.random() * 1.5;
                humidityVariation = Math.cos(hour * Math.PI / 12) * 10 + Math.random() * 5;
                break;
            case 'ecmwf':
                tempVariation = Math.sin(hour * Math.PI / 12) * 4 + Math.random() * 1.5;
                windVariation = Math.sin((hour + 4) * Math.PI / 12) * 2.5 + Math.random() * 1;
                humidityVariation = Math.cos(hour * Math.PI / 12) * 8 + Math.random() * 3;
                break;
            case 'model1':
                tempVariation = Math.sin(hour * Math.PI / 12) * 6 + Math.random() * 2.5;
                windVariation = Math.sin((hour + 8) * Math.PI / 12) * 4 + Math.random() * 2;
                humidityVariation = Math.cos(hour * Math.PI / 12) * 12 + Math.random() * 6;
                break;
            case 'model2':
                tempVariation = Math.sin(hour * Math.PI / 12) * 4.5 + Math.random() * 1.8;
                windVariation = Math.sin((hour + 2) * Math.PI / 12) * 3.5 + Math.random() * 1.2;
                humidityVariation = Math.cos(hour * Math.PI / 12) * 9 + Math.random() * 4;
                break;
            case 'model3':
                tempVariation = Math.sin(hour * Math.PI / 12) * 5.5 + Math.random() * 2.2;
                windVariation = Math.sin((hour + 10) * Math.PI / 12) * 3.8 + Math.random() * 1.8;
                humidityVariation = Math.cos(hour * Math.PI / 12) * 11 + Math.random() * 5.5;
                break;
            default:
                tempVariation = Math.random() * 3;
                windVariation = Math.random() * 2;
                humidityVariation = Math.random() * 5;
        }

        baseData.push({
            time: `${hour.toString().padStart(2, '0')}:00`,
            temperature: Math.round((20 + tempVariation) * 10) / 10,
            windSpeed: Math.round((5 + windVariation) * 10) / 10,
            humidity: Math.round((60 + humidityVariation)),
            pressure: Math.round((1013 + Math.sin(hour * Math.PI / 12) * 5 + Math.random() * 2) * 10) / 10,
            precipitation: Math.max(0, Math.round((Math.random() - 0.8) * 10)),
        });
    }

    return baseData;
};

// 多维度准确度数据 - 体现大模型优势
const generateAccuracyData = () => [
    {
        source: '广西气象局',
        temperature: 87.2,
        windSpeed: 82.5,
        humidity: 85.1,
        pressure: 84.8,
        precipitation: 78.3,
        extreme: 72.5,
        longTerm: 79.2,
        overall: 82.2
    },
    {
        source: '欧洲气象局',
        temperature: 89.8,
        windSpeed: 86.3,
        humidity: 88.7,
        pressure: 87.9,
        precipitation: 83.1,
        extreme: 76.8,
        longTerm: 84.5,
        overall: 85.3
    },
    {
        source: '大模型1',
        temperature: 94.2,
        windSpeed: 92.8,
        humidity: 93.6,
        pressure: 93.1,
        precipitation: 91.4,
        extreme: 89.7,
        longTerm: 92.3,
        overall: 92.4
    },
    {
        source: '大模型2',
        temperature: 95.8,
        windSpeed: 94.5,
        humidity: 94.9,
        pressure: 94.2,
        precipitation: 93.6,
        extreme: 92.8,
        longTerm: 94.1,
        overall: 94.3
    },
    {
        source: '大模型3',
        temperature: 94.6,
        windSpeed: 93.2,
        humidity: 93.8,
        pressure: 93.7,
        precipitation: 92.1,
        extreme: 90.5,
        longTerm: 93.2,
        overall: 93.0
    }
];

// 地图区域数据 - 中国南部地区
const mapRegions = [
    { id: 1, name: '南宁', lat: 22.82, lng: 108.32, temp: 28.5, wind: 6.2, windPower: 450, status: 'normal' },
    { id: 2, name: '桂林', lat: 25.29, lng: 110.28, temp: 26.8, wind: 4.8, windPower: 320, status: 'warning' },
    { id: 3, name: '柳州', lat: 24.33, lng: 109.41, temp: 27.2, wind: 5.5, windPower: 380, status: 'normal' },
    { id: 4, name: '北海', lat: 21.47, lng: 109.12, temp: 29.1, wind: 7.8, windPower: 680, status: 'high' },
    { id: 5, name: '玉林', lat: 22.63, lng: 110.15, temp: 28.8, wind: 5.2, windPower: 350, status: 'normal' },
    { id: 6, name: '梧州', lat: 23.48, lng: 111.28, temp: 27.5, wind: 4.2, windPower: 280, status: 'low' },
    { id: 7, name: '钦州', lat: 21.95, lng: 108.62, temp: 28.9, wind: 6.8, windPower: 520, status: 'normal' },
    { id: 8, name: '贵港', lat: 23.10, lng: 109.60, temp: 27.8, wind: 5.0, windPower: 340, status: 'normal' },
];

// Wind power density ranges for heat map colors
const getWindPowerColor = (power: number) => {
    if (power < 200) return '#4a90e2'; // Low - Blue
    if (power < 300) return '#7bb3f0'; // Low-Medium - Light Blue  
    if (power < 400) return '#50c878'; // Medium - Green
    if (power < 500) return '#ffd700'; // Medium-High - Yellow
    if (power < 600) return '#ff8c00'; // High - Orange
    return '#ff4500'; // Very High - Red
};

// Generate heat map grid data
const generateHeatMapData = (source: string) => {
    const heatData = [];
    for (let i = 0; i < 15; i++) {
        for (let j = 0; j < 15; j++) {
            const baseValue = Math.sin(i * 0.3) * Math.cos(j * 0.3) * 400 + 300;
            let sourceVariation = 1;

            switch (source) {
                case 'guangxi': sourceVariation = 1 + Math.random() * 0.3; break;
                case 'ecmwf': sourceVariation = 0.9 + Math.random() * 0.4; break;
                case 'model1': sourceVariation = 1.1 + Math.random() * 0.5; break;
                case 'model2': sourceVariation = 0.95 + Math.random() * 0.35; break;
                case 'model3': sourceVariation = 1.05 + Math.random() * 0.45; break;
                default: sourceVariation = 1;
            }

            heatData.push({
                x: i,
                y: j,
                value: Math.max(50, baseValue * sourceVariation),
                intensity: Math.min(1, (baseValue * sourceVariation) / 800)
            });
        }
    }
    return heatData;
};

// 圆形渐变热力图组件
const HeatMapOverlay: React.FC<{ data: any[], layer: string }> = ({ data, layer }) => {
    // 创建渐变定义
    const getGradientId = (index: number) => `gradient-${index}`;

    return (
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1000
        }}>
            <svg width="100%" height="100%" style={{ position: 'absolute' }}>
                <defs>
                    {data.map((cell, index) => {
                        let centerColor = '#1890ff';
                        if (layer === 'wind') {
                            centerColor = getWindPowerColor(cell.value);
                        } else if (layer === 'temperature') {
                            const tempIntensity = Math.min(255, cell.intensity * 255);
                            centerColor = `rgb(255, ${255 - tempIntensity}, 0)`;
                        } else {
                            const pressureIntensity = Math.min(255, cell.intensity * 200);
                            centerColor = `rgb(0, ${255 - pressureIntensity}, 255)`;
                        }

                        return (
                            <radialGradient
                                key={index}
                                id={getGradientId(index)}
                                cx="50%" cy="50%" r="80%"
                            >
                                <stop offset="0%" stopColor={centerColor} stopOpacity={cell.intensity * 0.8} />
                                <stop offset="30%" stopColor={centerColor} stopOpacity={cell.intensity * 0.5} />
                                <stop offset="60%" stopColor={centerColor} stopOpacity={cell.intensity * 0.3} />
                                <stop offset="100%" stopColor={centerColor} stopOpacity="0" />
                            </radialGradient>
                        );
                    })}
                </defs>

                {data.map((cell, index) => {
                    const x = (cell.x / 15) * 100;
                    const y = (cell.y / 15) * 100;
                    const size = 100 / 15;
                    const radius = (size * 0.8);

                    return (
                        <circle
                            key={index}
                            cx={`${x + size / 2}%`}
                            cy={`${y + size / 2}%`}
                            r={`${radius}%`}
                            fill={`url(#${getGradientId(index)})`}
                        />
                    );
                })}
            </svg>
        </div>
    );
};

const WeatherPrediction: React.FC = () => {
    const [selectedSource, setSelectedSource] = useState<string>('guangxi');
    const [weatherData, setWeatherData] = useState<any[]>([]);
    const [accuracyData, setAccuracyData] = useState<any[]>([]);
    const [heatMapData, setHeatMapData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [mapLayer, setMapLayer] = useState<'wind' | 'temperature' | 'pressure'>('wind');

    const dataSources = [
        { value: 'guangxi', label: '广西气象局', color: '#1890ff' },
        { value: 'ecmwf', label: '欧洲气象局', color: '#52c41a' },
        { value: 'model1', label: '大模型1', color: '#faad14' },
        { value: 'model2', label: '大模型2', color: '#f5222d' },
        { value: 'model3', label: '大模型3', color: '#722ed1' },
    ];

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            // 模拟加载延迟
            await new Promise(resolve => setTimeout(resolve, 800));

            const newWeatherData = generateWeatherData(selectedSource);
            const newAccuracyData = generateAccuracyData();
            const newHeatMapData = generateHeatMapData(selectedSource);

            setWeatherData(newWeatherData);
            setAccuracyData(newAccuracyData);
            setHeatMapData(newHeatMapData);
            setLoading(false);
        };

        loadData();
    }, [selectedSource]);

    const currentSource = dataSources.find(s => s.value === selectedSource);

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '24px' }}>
                <Title level={2}>
                    <CloudOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    气象预测分析
                </Title>
                <Paragraph type="secondary">
                    多源气象数据融合预测，支持多个气象数据源的对比和分析
                </Paragraph>
            </div>

            {/* 数据源选择和图层控制 */}
            <Card style={{ marginBottom: '24px' }}>
                <Row gutter={[24, 16]}>
                    <Col span={12}>
                        <Space align="center">
                            <span style={{ fontWeight: 'bold' }}>数据源:</span>
                            <Select
                                value={selectedSource}
                                onChange={setSelectedSource}
                                style={{ width: 200 }}
                                size="large"
                            >
                                {dataSources.map(source => (
                                    <Option key={source.value} value={source.value}>
                                        <Tag color={source.color} style={{ marginRight: '8px' }}>●</Tag>
                                        {source.label}
                                    </Option>
                                ))}
                            </Select>
                            <Tag color={currentSource?.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
                                当前: {currentSource?.label}
                            </Tag>
                        </Space>
                    </Col>
                    <Col span={12}>
                        <Space align="center">
                            <span style={{ fontWeight: 'bold' }}>地图图层:</span>
                            <Select
                                value={mapLayer}
                                onChange={setMapLayer}
                                style={{ width: 160 }}
                                size="large"
                            >
                                <Option value="wind">风力密度</Option>
                                <Option value="temperature">温度</Option>
                                <Option value="pressure">气压</Option>
                            </Select>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Spin spinning={loading}>
                <Row gutter={[24, 24]}>
                    {/* Wind Atlas Style Map */}
                    <Col span={24}>
                        <Card
                            title={
                                <span>
                                    <EnvironmentOutlined style={{ marginRight: '8px' }} />
                                    全球风力图谱 - 中国南部地区
                                </span>
                            }
                            style={{ height: '600px' }}
                        >
                            <div style={{
                                height: '520px',
                                borderRadius: '8px',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                {/* Real Map with Leaflet */}
                                <MapContainer
                                    center={[23.5, 109.5]} // Center on Guangxi region
                                    zoom={7}
                                    style={{ height: '100%', width: '100%' }}
                                    zoomControl={true}
                                >
                                    {/* OpenStreetMap tiles */}
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    />

                                    {/* City markers */}
                                    {mapRegions.map((region) => (
                                        <CircleMarker
                                            key={region.id}
                                            center={[region.lat, region.lng]}
                                            radius={8}
                                            fillColor={getWindPowerColor(region.windPower)}
                                            color="#fff"
                                            weight={2}
                                            opacity={1}
                                            fillOpacity={0.8}
                                        >
                                            <Popup>
                                                <div style={{ textAlign: 'center', minWidth: '120px' }}>
                                                    <strong>{region.name}</strong><br />
                                                    温度: {region.temp}°C<br />
                                                    风速: {region.wind} m/s<br />
                                                    风力密度: {region.windPower} W/m²
                                                </div>
                                            </Popup>
                                        </CircleMarker>
                                    ))}
                                </MapContainer>

                                {/* Heat map overlay */}
                                <HeatMapOverlay data={heatMapData} layer={mapLayer} />

                                {/* Map controls */}
                                <div style={{
                                    position: 'absolute',
                                    top: '10px',
                                    left: '10px',
                                    background: 'rgba(0, 0, 0, 0.8)',
                                    color: '#fff',
                                    padding: '6px 10px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    zIndex: 1001
                                }}>
                                    {mapLayer === 'wind' ? '风力密度 (W/m²)' :
                                        mapLayer === 'temperature' ? '温度 (°C)' : '气压 (hPa)'}
                                </div>

                                {/* Color scale legend */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: '10px',
                                    right: '10px',
                                    background: 'rgba(0, 0, 0, 0.8)',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    fontSize: '10px',
                                    zIndex: 1001
                                }}>
                                    <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>比例尺</div>
                                    {mapLayer === 'wind' && (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                                                <div style={{ width: '12px', height: '12px', background: '#4a90e2', marginRight: '4px' }}></div>
                                                <span>&lt;200</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                                                <div style={{ width: '12px', height: '12px', background: '#50c878', marginRight: '4px' }}></div>
                                                <span>200-400</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                                                <div style={{ width: '12px', height: '12px', background: '#ffd700', marginRight: '4px' }}></div>
                                                <span>400-600</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ width: '12px', height: '12px', background: '#ff4500', marginRight: '4px' }}></div>
                                                <span>&gt;600</span>
                                            </div>
                                        </div>
                                    )}
                                    {mapLayer !== 'wind' && (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                                                <div style={{ width: '12px', height: '12px', background: 'rgba(0,100,255,0.5)', marginRight: '4px' }}></div>
                                                <span>低</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                                                <div style={{ width: '12px', height: '12px', background: 'rgba(255,200,0,0.7)', marginRight: '4px' }}></div>
                                                <span>中</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ width: '12px', height: '12px', background: 'rgba(255,0,0,0.8)', marginRight: '4px' }}></div>
                                                <span>高</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Data source attribution */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: '10px',
                                    left: '10px',
                                    background: 'rgba(0, 0, 0, 0.8)',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '9px',
                                    zIndex: 1001
                                }}>
                                    数据源: {currentSource?.label} | 分辨率: 250m
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* Weather Forecast Charts */}
                {/* <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
                    <Col span={24}>
                        <Card
                            title={
                                <span>
                                    <ThunderboltOutlined style={{ marginRight: '8px' }} />
                                    24小时气象预测曲线
                                </span>
                            }
                        >
                            <ResponsiveContainer width="100%" height={400}>
                                <LineChart data={weatherData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="time" />
                                    <YAxis yAxisId="temp" orientation="left" />
                                    <YAxis yAxisId="wind" orientation="right" />
                                    <Tooltip
                                        formatter={(value, name) => [
                                            `${value}${name === 'temperature' ? '°C' : name === 'windSpeed' ? 'm/s' : name === 'humidity' ? '%' : 'hPa'}`,
                                            name === 'temperature' ? '温度' : name === 'windSpeed' ? '风速' : name === 'humidity' ? '湿度' : '气压'
                                        ]}
                                    />
                                    <Legend
                                        formatter={(value) =>
                                            value === 'temperature' ? '温度 (°C)' :
                                                value === 'windSpeed' ? '风速 (m/s)' :
                                                    value === 'humidity' ? '湿度 (%)' : '气压 (hPa)'
                                        }
                                    />
                                    <Line yAxisId="temp" type="monotone" dataKey="temperature" stroke="#1890ff" strokeWidth={2} dot={{ r: 4 }} />
                                    <Line yAxisId="wind" type="monotone" dataKey="windSpeed" stroke="#52c41a" strokeWidth={2} dot={{ r: 4 }} />
                                    <Line yAxisId="temp" type="monotone" dataKey="humidity" stroke="#722ed1" strokeWidth={2} dot={{ r: 4 }} />
                                    <Line yAxisId="temp" type="monotone" dataKey="pressure" stroke="#faad14" strokeWidth={2} dot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>
                </Row> */}

                {/* 多维度准确度对比 */}
                <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
                    <Col span={24}>
                        <Card
                            title="数据源预测准确度对比 - 多维度性能分析"
                            extra={
                                <Tag color="green" style={{ fontSize: '12px' }}>
                                    大模型在各维度均表现优异
                                </Tag>
                            }
                        >
                            <Row gutter={[16, 16]}>
                                {/* 基础气象要素对比 */}
                                <Col span={12}>
                                    <div style={{ marginBottom: '16px' }}>
                                        <strong style={{ fontSize: '14px', color: '#1890ff' }}>基础气象要素预测准确度</strong>
                                    </div>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={accuracyData} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="source"
                                                angle={-45}
                                                textAnchor="end"
                                                height={60}
                                                interval={0}
                                                fontSize={11}
                                            />
                                            <YAxis domain={[70, 100]} />
                                            <Tooltip formatter={(value) => [`${value}%`, '准确度']} />
                                            <Legend />
                                            <Bar dataKey="temperature" fill="#1890ff" name="温度" />
                                            <Bar dataKey="windSpeed" fill="#52c41a" name="风速" />
                                            <Bar dataKey="humidity" fill="#722ed1" name="湿度" />
                                            <Bar dataKey="pressure" fill="#fa8c16" name="气压" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Col>

                                {/* 高级预测能力对比 */}
                                <Col span={12}>
                                    <div style={{ marginBottom: '16px' }}>
                                        <strong style={{ fontSize: '14px', color: '#52c41a' }}>高级预测能力对比</strong>
                                    </div>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={accuracyData} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="source"
                                                angle={-45}
                                                textAnchor="end"
                                                height={60}
                                                interval={0}
                                                fontSize={11}
                                            />
                                            <YAxis domain={[70, 100]} />
                                            <Tooltip formatter={(value) => [`${value}%`, '准确度']} />
                                            <Legend />
                                            <Bar dataKey="precipitation" fill="#13c2c2" name="降水预测" />
                                            <Bar dataKey="extreme" fill="#f5222d" name="极端天气" />
                                            <Bar dataKey="longTerm" fill="#eb2f96" name="长期预测" />
                                            <Bar dataKey="overall" fill="#faad14" name="综合评分" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Col>
                            </Row>

                            {/* 性能优势总结 */}
                            <div style={{
                                marginTop: '24px',
                                padding: '16px',
                                background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%)',
                                borderRadius: '8px',
                                border: '1px solid #b7eb8f'
                            }}>
                                <Title level={4} style={{ marginBottom: '12px', color: '#52c41a' }}>
                                    🚀 大模型性能优势分析
                                </Title>
                                <Row gutter={[16, 8]}>
                                    <Col span={8}>
                                        <div style={{ textAlign: 'center', padding: '8px' }}>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>+8.7%</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>基础预测提升</div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ textAlign: 'center', padding: '8px' }}>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>+16.2%</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>极端天气预测</div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ textAlign: 'center', padding: '8px' }}>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>+9.8%</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>长期预测能力</div>
                                        </div>
                                    </Col>
                                </Row>
                                <div style={{ marginTop: '12px', fontSize: '13px', color: '#666', textAlign: 'center' }}>
                                    ✨ 大模型通过深度学习和海量数据训练，在复杂天气模式识别和极端事件预测方面展现出显著优势
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Spin>
        </div>
    );
};

export default WeatherPrediction; 